import { useMemo, useState } from "react";
import {
  Building2,
  GraduationCap,
  Search,
  ShieldCheck,
  Users,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { PageHeader } from "@/components/page-header";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTeams, useTenants } from "@/lib/queries";
import { useSessionContext } from "@/lib/session";

import { createFileRoute } from "@tanstack/react-router";
function ManagerTeamsPage() {
  const { tenantId } = useSessionContext();
  const { data: tenants = [] } = useTenants();
  const { data: teams = [] } = useTeams();
  const sparkasse = tenants.find((tenant) => tenant.id === tenantId);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return teams;
    return teams.filter((t) =>
      [t.name, t.lead, t.focus].join(" ").toLowerCase().includes(q)
    );
  }, [query, teams]);

  const totals = filtered.reduce(
    (acc, t) => ({ users: acc.users + t.users }),
    { users: 0 }
  );

  return (
    <div className="space-y-7">
      <PageHeader
        eyebrow={
          <>
            <Building2 className="h-3.5 w-3.5" />
            {sparkasse?.name ?? "Sparkasse"} · Gruppen
          </>
        }
        title="Gruppen & Lernfokus"
        description="Gruppen, Leitung und aktueller Lernfokus dieser Sparkasse."
      />

      <section className="grid gap-3 md:grid-cols-3">
        <Metric icon={<Users className="h-3.5 w-3.5" />} label="Gruppen" value={String(filtered.length)} />
        <Metric icon={<GraduationCap className="h-3.5 w-3.5" />} label="Nutzer" value={String(totals.users)} />
        <Metric icon={<ShieldCheck className="h-3.5 w-3.5" />} label="Region" value={sparkasse?.region ?? "—"} />
      </section>

      <Card className="gap-0 overflow-hidden border-[var(--rule)] p-0">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--rule)] bg-[var(--paper)]/45 px-5 py-4">
          <div>
            <div className="eyebrow">Gruppensteuerung</div>
            <h2 className="mt-1 font-display text-[20px] font-medium tracking-tight">
              Gruppen in dieser Sparkasse
            </h2>
          </div>
          <div className="relative w-full sm:w-[280px]">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Gruppe, Leitung, Fokus…"
              className="h-9 pl-8 text-[14px]"
            />
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Gruppe</TableHead>
              <TableHead>Leitung</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Nutzer</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-medium">{row.name}</TableCell>
                <TableCell>{row.lead}</TableCell>
                <TableCell className="space-x-1.5">
                  {row.pilot && (
                    <Badge variant="outline" className="border-primary/25 bg-primary/10 text-[12px] text-primary">
                      Pilot
                    </Badge>
                  )}
                  <Badge variant="outline" className="text-[12px]">
                    {row.status === "active" ? "aktiv" : "pausiert"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {row.users}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function Metric({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <Card className="reveal gap-0 border-[var(--rule)] p-5">
      <div className="flex items-center gap-1.5 font-bold text-[11px] tracking-[0.06em] text-muted-foreground uppercase">
        {icon}
        {label}
      </div>
      <div className="tnum mt-3 font-display text-[34px] leading-none font-medium tracking-tight">
        {value}
      </div>
    </Card>
  );
}

export const Route = createFileRoute("/_app/management/teams")({
  component: ManagerTeamsPage,
});
